import { theme, type ThemeConfig } from 'antd';
import { BRAND } from '@/constants/brand';
import { SHELL_PALETTES, type ThemeMode } from './appShellTheme';

/**
 * antd derives every generated state (hover, disabled, border, elevated surface,
 * focus ring) from the algorithm and the base tokens, so each mode gets its own
 * algorithm rather than one palette repainted with global `!important` overrides.
 *
 * Takes resolved colours, not the `var()` references in APP_SHELL_THEME: the
 * algorithm does colour maths on these and cannot read a CSS variable.
 */
const getAntdConfig = (mode: ThemeMode): ThemeConfig => {
  const shell = SHELL_PALETTES[mode];

  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      fontFamily: BRAND.fontFamily,
      fontSize: 14,
      // Bases the algorithm derives every other surface/text shade from.
      colorBgBase: mode === 'dark' ? shell.pageBg : shell.panelBg,
      colorBgLayout: shell.pageBg,
      colorTextBase: shell.text,
      // Lavanda, not electric: electric (#0b1775) is near-black on an ink background
      // and fails contrast for interactive elements.
      colorPrimary: shell.accent,
      // Link text goes a step further from the background than the accent. On ink,
      // lavanda as text is 3.78:1 — enough to make "Edit" read as disabled next to
      // the white "Delete" beside it in table action columns.
      colorLink: shell.accentText,
      colorLinkHover: shell.accentTextHover,
      colorSuccess: shell.positive,
      colorError: shell.negative,
      colorWarning: BRAND.hacienda,
      colorBorder: shell.panelBorder,
      borderRadius: 6
    },
    components: {
      Table: {
        filterDropdownBg: shell.tableFixedHeaderBg,
        filterDropdownMenuBg: shell.tableFixedHeaderBg
      },
      Input: {
        addonBg: shell.filterInputBg
      },
      InputNumber: {
        addonBg: shell.filterInputBg
      },
      Dropdown: {
        colorBgElevated: shell.tableFixedHeaderBg,
        colorText: shell.text
      },
      DatePicker: {
        colorBgElevated: shell.panelBg,
        colorText: shell.text,
        colorTextDisabled: shell.textDisabled,
        colorPrimary: shell.accent,
        cellHoverBg: 'rgba(97, 97, 215, 0.28)',
        cellActiveWithRangeBg: 'rgba(97, 97, 215, 0.32)',
        cellHoverWithRangeBg: 'rgba(97, 97, 215, 0.22)',
        cellRangeBorderColor: shell.accent
      }
    }
  };
};

export default getAntdConfig;
